import React, { useRef, useState, useEffect } from "react";
import { api, API_BASE } from "../lib/api";
import { Camera, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * CameraCapture — opens the device camera in-page, lets the user take a photo,
 * uploads it to /api/upload and returns the stored URL via onCapture(url).
 * Works on laptops (webcam) and phones (front camera by default).
 */
export default function CameraCapture({ onCapture, kind = "attendance", facing = "user" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  // Always release the camera when the component unmounts.
  useEffect(() => () => stop(), []);

  const start = async () => {
    setError("");
    setOpen(true);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera isn't supported on this device/browser. Use the upload option instead.");
      return;
    }
    if (!window.isSecureContext) {
      setError("The camera needs a secure (https) connection. Use the upload option instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access in your browser and try again."
          : e?.name === "NotFoundError"
          ? "No camera found on this device. Use the upload option instead."
          : e?.message || "Could not open the camera."
      );
    }
  };

  const close = () => { stop(); setOpen(false); setError(""); };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { toast.error("Camera isn't ready yet — give it a second"); return; }
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      const form = new FormData();
      form.append("file", new File([blob], `selfie_${Date.now()}.jpg`, { type: "image/jpeg" }));
      form.append("kind", kind);
      const { data } = await api.post("/api/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onCapture(`${API_BASE.replace(/\/api$/, "")}${data.url}`);
      toast.success("Selfie captured");
      close();
    } catch (e) {
      toast.error(`Could not save the selfie: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="camera-capture">
      {!open ? (
        <button type="button" onClick={start} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="camera-open">
          <Camera size={14} /> Open camera &amp; take selfie
        </button>
      ) : (
        <div className="border border-line bg-black/5 p-2">
          {error ? (
            <div className="text-xs text-red-600 p-3 text-center">{error}</div>
          ) : (
            <div className="relative bg-black overflow-hidden rounded">
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full max-h-72 object-contain"
                style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
              />
            </div>
          )}
          <div className="flex gap-2 mt-2">
            {!error && (
              <button type="button" onClick={capture} disabled={busy} className="btn-maroon text-xs flex-1 justify-center inline-flex items-center gap-1" data-testid="camera-capture-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Capture
              </button>
            )}
            <button type="button" onClick={close} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="camera-close">
              <X size={14} /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
